import { useEffect, useState } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Image from 'next/image'

// Components
import Popup from '@/components/common/Popup'

// Constants
import { ACCOUNT_NEED_UPGRADE_EVENT } from '@/constants/errorData'
import { ROUTE_PATH } from '@/constants/routePath'

// Methods
import handleLogout from '@/methods/handleLogout'

// Redux
import { useAppSelector } from '@/redux/hooks'

// Styles
import registerStyles from '@/components/pages/ShopRegister/style.module.scss'

// Variables
const cx = classnames.bind(registerStyles)
const EXCLUDED_PATHS = [ROUTE_PATH.LOGIN, ROUTE_PATH.UPGRADE, ROUTE_PATH.BRAND_LIVE_ROOM]

// Shop 自建登入 v2：v1 帳號的 token 打需登入 API（如 fetchMemberData）被擋下回 -20060 時，
// service.js 發出 ACCOUNT_NEED_UPGRADE_EVENT，這裡跳「帳號升級」Dialog。
// 動作只有「驗證」（導去 /upgrade 的手機驗證碼畫面）與「登出」，不可關閉
const AccountUpgradeDialog = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const memberData = useAppSelector((state) => state.member.memberData)

  // 排除：/login 有自己的升級 Popup、/upgrade 是升級流程本身、品牌直播間（第三方品牌頁不跳 Shop 帳號升級）
  const isExcludedPath = EXCLUDED_PATHS.includes(router.pathname)

  useEffect(() => {
    if (isExcludedPath) return
    const handleNeedUpgrade = () => setIsOpen(true)

    window.addEventListener(ACCOUNT_NEED_UPGRADE_EVENT, handleNeedUpgrade)

    return () => window.removeEventListener(ACCOUNT_NEED_UPGRADE_EVENT, handleNeedUpgrade)
  }, [isExcludedPath])

  // profile 的 needs_upgrade: true 也要全站跳（不只靠 API 錯誤碼 -20060 的事件）。
  // LiveMallClub 系統沒有升級頁（ROUTE_PATH.UPGRADE 為 undefined），不跳
  useEffect(() => {
    if (!ROUTE_PATH.UPGRADE) return
    if (isExcludedPath) return
    if (memberData?.needsUpgrade) setIsOpen(true)
  }, [memberData?.needsUpgrade, isExcludedPath])

  const handleVerify = () => {
    setIsOpen(false)
    // SDC-2275：把當前頁帶進升級流程，升級完成（GiftStepPanel → /me 完善資料 → 領禮）後導回原頁
    router.push({ pathname: ROUTE_PATH.UPGRADE, query: { redirect: router.asPath } })
  }

  // 與 ShopLogin/ShopUpgrade 同一套規則：有任何角色(商家/管理員/KOL)走 email 管道文案，一般會員走 phone
  const hasSpecialRole = Array.isArray(memberData?.roles) && memberData.roles.length > 0

  return (
    <Popup
      title={t('Upgrade_Title')}
      isOpen={isOpen}
      hasConfirm={false}
      hasCancel={false}
      canClose={false}
      wrapperClassname={cx('register-popup')}
      icon={
        <div className={cx('register-popup-icon')}>
          <Image src='/images/shield-check-icon.svg' alt='' width={34} height={34} />
        </div>
      }
    >
      <p className={cx('register-popup-description')} style={{ whiteSpace: 'pre-line' }}>
        {t(hasSpecialRole ? 'Upgrade_Dialog_Description_Email' : 'Upgrade_Dialog_Description_Phone')}
      </p>
      <div className={cx('register-email-popup-actions')}>
        <button type='button' className={cx('register-email-popup-confirm')} onClick={handleVerify}>
          {t('Upgrade_Dialog_Verify')}
        </button>
        {/* SDC-2599：手機版也要有「登出」（Figma 9499:43713 原本只有「驗證」） */}
        <button type='button' className={cx('register-email-popup-cancel')} onClick={() => handleLogout()}>
          {t('Header_Logout')}
        </button>
      </div>
    </Popup>
  )
}

export default AccountUpgradeDialog
